import { AppSettings } from '@/AppSettings';
import { crfReadOnly } from '@/utils/CrfFormUtils';
import { TransferEventForm } from '@/api/dtos';
import type { Patient } from '@/api/dtos';
import { ValidateOn } from '@ngt/forms-core';
import {
    Autocomplete,
    CollaboratingGroupContext,
    CrfForm,
    DatePicker,
    FormBreadcrumbs,
    GroupedFields,
    GroupedTableArrayField,
    IGroupedTableArrayFieldColumn,
    ILookupItem,
    Input,
    InstitutionContext,
    InstitutionsContext,
    MasterGroupContext,
    OnlinePatientManagementContext,
    PageLayout,
    PatientContext,
    RouteLoading,
} from '@ngt/opms';
import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';
import { useSnackbar } from 'notistack';
import { FC, useCallback, useContext, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

const TransferFields: FC = () => {
    const { data: institutions } = useContext(InstitutionsContext);

    const institutionOptions = useMemo<ILookupItem[] | undefined>(() => institutions
        ?.filter(institution => institution.id != null && institution.activatedDate != null)
        .sort((left, right) => left.name.localeCompare(right.name))
        .map((institution, index) => ({
            id: institution.id,
            value: `${institution.name}`,
            order: index + 1,
            type: 1,
        })), [institutions]);

    const columns = useMemo<Array<IGroupedTableArrayFieldColumn<TransferEventForm>>>(() => [
        {
            propertyPath: 'dateBctAdvised',
            content: <Input component={DatePicker} />,
            TableHeadCellProps: { sx: { minWidth: 190 } },
            TableCellProps: { sx: { minWidth: 190 } },
        },
        {
            propertyPath: 'institutionId',
            content: <Input component={Autocomplete} options={institutionOptions} />,
            TableHeadCellProps: { sx: { minWidth: 320 } },
            TableCellProps: { sx: { minWidth: 320 } },
        },
        {
            propertyPath: 'referralLetterDate',
            content: <Input component={DatePicker} />,
            TableHeadCellProps: { sx: { minWidth: 190 } },
            TableCellProps: { sx: { minWidth: 190 } },
        },
        {
            propertyPath: 'consentSignedDate',
            content: <Input component={DatePicker} />,
            TableHeadCellProps: { sx: { minWidth: 190 } },
            TableCellProps: { sx: { minWidth: 190 } },
        },
        {
            propertyPath: 'transferConfirmedDate',
            content: <Input component={DatePicker} />,
            TableHeadCellProps: { sx: { minWidth: 190 } },
            TableCellProps: { sx: { minWidth: 190 } },
        },
        {
            propertyPath: 'firstFollowUpDate',
            content: <Input component={DatePicker} />,
            TableHeadCellProps: { sx: { minWidth: 190 } },
            TableCellProps: { sx: { minWidth: 190 } },
        },
    ], [institutionOptions]);

    return (
        <GroupedFields>
            <GroupedTableArrayField<TransferEventForm>
                entityName="Transfer"
                propertyPath="transfers"
                columns={columns}
                AddButtonProps={{ sx: { mb: 2 } }}
            />
        </GroupedFields>
    );
};

const TransferForm: FC = () => {
    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: collaboratingGroup } = useContext(CollaboratingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: patient, mutate: mutatePatient } = useContext(PatientContext);
    const { routeParameters } = useContext(OnlinePatientManagementContext);
    const { enqueueSnackbar } = useSnackbar();
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();

    const refreshPatientAndNavigate = useCallback(async (returnToSummary: boolean) => {
        if (!patient?.id) {
            enqueueSnackbar('Unable to reload the participant because their ID is unavailable.', { variant: 'error' });
            return;
        }

        let updatedPatient: Patient | undefined;
        try {
            // Revalidate once and use the returned response before navigating.
            const response = await mutatePatient();
            if (response?.responseStatus?.errorCode) {
                enqueueSnackbar(response.responseStatus.message ?? 'Unable to reload the participant after saving.', { variant: 'error' });
                return;
            }
            updatedPatient = response?.patient as Patient | undefined;
        } catch {
            enqueueSnackbar('Unable to reload the participant after saving.', { variant: 'error' });
            return;
        }

        if (!updatedPatient?.studyNumber || !updatedPatient.institutionCode) {
            enqueueSnackbar('Unable to reload the participant after saving.', { variant: 'error' });
            return;
        }

        const patientPath = `/registration/${encodeURIComponent(updatedPatient.institutionCode)}/${encodeURIComponent(updatedPatient.studyNumber)}`;

        if (returnToSummary) {
            navigate(patientPath, { replace: true });
            return;
        }

        const eventDefinitionCode = params[routeParameters.eventDefinitionCode];
        const eventRepeat = params[routeParameters.eventRepeat];
        const formDefinitionCode = params[routeParameters.formDefinitionCode];
        const formRepeat = params[routeParameters.formRepeat];

        if (!eventDefinitionCode || !eventRepeat || !formDefinitionCode || !formRepeat) {
            enqueueSnackbar('Unable to update the form URL after saving.', { variant: 'error' });
            return;
        }

        navigate({
            pathname: `${patientPath}/${encodeURIComponent(eventDefinitionCode)}/${encodeURIComponent(eventRepeat)}/${encodeURIComponent(formDefinitionCode)}/${encodeURIComponent(formRepeat)}`,
            search: location.search,
            hash: location.hash,
        }, { replace: true });
    }, [enqueueSnackbar, location.hash, location.search, mutatePatient, navigate, params, patient?.id, routeParameters]);

    const {
        permissions: [canUpdateCrf],
        loading: permissionsLoading,
    } = usePermissionsByIds(
        [Permission.OpmsCrfUpdate],
        masterGroup?.id,
        collaboratingGroup?.id,
        institution?.id,
        patient?.id
    );

    if (permissionsLoading) {
        return <RouteLoading />;
    }

    return (
        <PageLayout breadcrumbs={<FormBreadcrumbs />}>
            <CrfForm
                validateOn={ValidateOn.onChange}
                readOnly={crfReadOnly(canUpdateCrf)}
                debug={AppSettings.debug}
                onAfterSubmit={async (_form, formState) => {
                    const { submitType } = formState.value as { submitType?: string };
                    await refreshPatientAndNavigate(submitType !== 'save');
                    // A defined result prevents CrfForm's default refresh and navigation.
                    return {};
                }}
            >
                <TransferFields />
            </CrfForm>
        </PageLayout>
    );
};

export default TransferForm;
